// safeLinksConfig.js
export const SAFE_LINK_CONFIG = {
    // Domains that are considered always safe
    globalTrustedDomains: [
        'github.com',
        'stackoverflow.com',
        'mozilla.org',
        'google.com',
        'microsoft.com',
        'apple.com',
        'linkedin.com',
        'twitter.com',
        'facebook.com',
        'youtube.com',
        'amazon.com',
        'cloudflare.com',
        'jquery.com',
        'bootstrap.com',
        'fontawesome.com',
    ],
    
    /* NOTES
    Consider integrating with threat intelligence feeds or services:
    - VirusTotal API
    - URLVoid
    - Google Safe Browsing API
    - Phishing databases
    */

    // Patterns that indicate high risk
    dangerousPatterns: [
        {
            pattern: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
            description: 'IP address instead of domain',
            risk: 'high'
        },
        {
            pattern: /[a-z0-9]{15,}/,
            description: 'Very long random strings',
            risk: 'high'
        },
        {
            pattern: /[a-z]{20,}/,
            description: 'Extremely long subdomains',
            risk: 'high'
        },
        {
            pattern: /(.)\1{4,}/,
            description: 'Repeated characters',
            risk: 'medium'
        },
        {
            pattern: /(bit\.ly|tinyurl|t\.co|goo\.gl|short\.link|tiny\.cc|ow\.ly|is\.gd)/i,
            description: 'URL shortener',
            risk: 'medium'
        },
        {
            pattern: /[0-9]{8,}/,
            description: 'Contains many numbers',
            risk: 'medium'
        },
        {
            pattern: /[a-z]+-[a-z]+-[0-9]{4,}/,
            description: 'Suspicious word-number combinations',
            risk: 'high'
        },
        {
            pattern: /[0-9a-f]{32,}/,
            description: 'Long hexadecimal strings (potential hash-based domains)',
            risk: 'high'
        },
        {
            pattern: /[.-]{2,}/,
            description: 'Multiple consecutive dots or dashes',
            risk: 'high'
        },
        {
            pattern: /xn--/,
            description: 'Internationalized domain names (punycode)',
            risk: 'medium'
        },
        {
            pattern: /(secure|login|account|verify|update|confirm|bank|paypal|ebay)/i,
            description: 'Common phishing keywords',
            risk: 'high'
        },
    ],
    
    // File extensions that are risky
    riskyExtensions: [
        // Executable files
        '.exe', '.scr', '.bat', '.cmd', '.com', '.pif', 
        '.vbs', '.js', '.jar', '.app', '.deb', '.pkg',
        '.dmg', '.msi', '.run',
        
        // Script files
        '.ps1', '.ps2', '.psm1', '.psd1',  // PowerShell
        '.wsf', '.wsh',                     // Windows Script Host
        '.hta',                             // HTML applications
        '.lnk',                             // Windows shortcuts
        '.reg',                             // Registry files
        
        // Archive files (can contain malware)
        '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2',
        
        // Disk images
        '.iso', '.img', '.vhd', '.vmdk',
        
        // Office files with macros
        '.docm', '.xlsm', '.pptm', '.dotm', '.xltm', '.potm',
        
        // Other potentially dangerous
        '.pdf',                 // PDFs can contain malicious content
        '.apk',                 // Android packages
        '.ipa',                 // iOS packages
        '.dll', '.sys', '.drv', // System files
        '.ocx', '.cpl'          // ActiveX controls
    ],
    
    // Suspicious TLDs
    suspiciousTlds: [
        // Free/cheap TLDs often abused
        '.tk', '.ml', '.ga', '.cf', '.click', '.download',
        '.loan', '.win', '.bid',
        
        // Additional high-risk TLDs
        '.top', '.work', '.date', '.racing', '.party', '.review',
        '.country', '.stream', '.trade', '.webcam', '.men',
        '.science', '.accountant', '.faith', '.cricket', '.space',
        '.pw', '.cc', '.ws', '.info', '.biz', '.mobi',
        
        // Recently identified risky TLDs
        '.site', '.online', '.store', '.tech', '.club', '.website', '.ru'
    ],

    // Look-alike domain attack types with corrected risk levels and improved descriptions
    lookAlikeAttackTypes: {
        'TLD-swap': {
            riskLevel: 'high', 
            description: 'Same domain name with different top-level domain (e.g., "example.com" vs "example.net")',
            explanation: 'Cybercriminals register your exact domain name with different extensions (.com, .net, .org) to intercept traffic and conduct phishing.'
        },
        'Subdomain': {
            riskLevel: 'high',
            description: 'Legitimate domain used as subdomain with dots inserted (e.g., "exam.ple.com" vs "example.com")',
            explanation: 'Attackers register domains that make your legitimate domain appear as a subdomain, exploiting user trust in familiar names.'
        },
        'Hyphenation': {
            riskLevel: 'medium',
            description: 'Hyphens added to separate parts of the domain (e.g., "pay-pal.com" vs "paypal.com")',
            explanation: 'Hyphens inserted into compound words to create believable variations. Often used when the original domain is unavailable.'
        },
        'Dictionary': {
            riskLevel: 'medium',
            description: 'Dictionary words added to create extended domains (e.g., "paypal-support.com" vs "paypal.com")',
            explanation: 'Legitimate-sounding extensions using business terminology. Exploits user assumptions about company subbrands or services.'
        },
        'Homoglyph': {
            riskLevel: 'high',
            description: 'Visually similar characters used for deception (e.g., "goog1e.com" - "l" vs "1", "rn" vs "m")',
            explanation: 'Attackers use characters that look identical or very similar to deceive users. Only applies to truly confusable characters.'
        },
        'Addition': {
            riskLevel: 'medium', 
            description: 'Extra characters added to the domain (e.g., "paypall.com" vs "paypal.com")',
            explanation: 'Common typosquatting technique where extra letters are added, often targeting plural forms or common typing errors.'
        },
        'Omission': {
            riskLevel: 'medium',
            description: 'Characters removed from the domain (e.g., "gogle.com" vs "google.com")',
            explanation: 'Characters are deliberately omitted to create domains that users might type when rushing or making typing errors.'
        },
        'Replacement': {
            riskLevel: 'high',
            description: 'Character replacement with different letters (e.g., "goggle.com" vs "google.com")',
            explanation: 'Strategic character substitutions that create plausible-looking domains. Distinct from homoglyphs as characters are not visually similar.'
        },
        'Various': {
            riskLevel: 'medium',
            description: 'Multiple attack techniques combined or other complex variations',
            explanation: 'Sophisticated attacks combining multiple techniques or using advanced methods not covered by standard categories. Use only for truly ambiguous cases.'
        }
    }
};


// Helper function to normalize domains (remove www prefix)
const normalizeDomain = (domain) => {
    if (!domain) return domain;
    return domain.replace(/^www\./, '');
};

// Helper function to get root domain (e.g., "sub.example.com" -> "example.com")
const getRootDomain = (domain) => {
    if (!domain) return domain;
    const normalized = normalizeDomain(domain);
    const parts = normalized.split('.');
    
    // For domains like "sub.example.com", return "example.com"
    // For domains like "example.com", return "example.com"
    if (parts.length >= 2) {
        return parts.slice(-2).join('.');
    }
    return normalized;
};

// Helper function to get domain name without TLD for similar domain detection
const getDomainNameOnly = (domain) => {
    if (!domain) return domain;
    const rootDomain = getRootDomain(domain);
    const parts = rootDomain.split('.');
    // Return only the domain name part (before the TLD)
    return parts[0];
};

// Helper function to check if domain matches trusted domain (with www handling)
const isDomainTrustedMatch = (domain, trustedDomains) => {
    const normalizedDomain = normalizeDomain(domain);
    
    return trustedDomains.some(trusted => {
        const normalizedTrusted = normalizeDomain(trusted);
        
        // Exact match
        if (domain === trusted || normalizedDomain === normalizedTrusted) {
            return true;
        }
        
        // Subdomain match (e.g., api.example.com matches example.com)
        if (domain.endsWith('.' + trusted) || domain.endsWith('.' + normalizedTrusted)) {
            return true;
        }
        
        // Reverse subdomain match (e.g., example.com matches api.example.com if api.example.com is trusted)
        if (trusted.endsWith('.' + domain) || normalizedTrusted.endsWith('.' + normalizedDomain)) {
            return true;
        }
        
        // www vs non-www match
        if (domain === 'www.' + normalizedTrusted || normalizedDomain === trusted) {
            return true;
        }
        
        return false;
    });
};

// Helper function to check for similar domains with different TLDs
const checkSimilarDomainTLD = (domain, trustedDomains) => {
    // First check if the domain is already trusted (to avoid false positives)
    if (isDomainTrustedMatch(domain, trustedDomains)) {
        return null;
    }
    
    const domainNameOnly = getDomainNameOnly(domain);
    if (!domainNameOnly) return null;
    
    for (const trusted of trustedDomains) {
        const trustedNameOnly = getDomainNameOnly(trusted);
        
        // Only flag if the domain names match but it's not the same root domain
        if (trustedNameOnly && 
            domainNameOnly === trustedNameOnly && 
            getRootDomain(domain) !== getRootDomain(trusted)) {
            return {
                attackType: 'TLD-swap',
                similarDomain: trusted,
                reason: `Similar to trusted domain "${trusted}" but with different TLD`
            };
        }
    }
    return null;
};

// Advanced look-alike domain detection functions
const detectHomoglyph = (domain, trustedDomain) => {
    // Strict homoglyph mappings - only visually confusable characters
    const confusableMap = {
        // Single character confusables
        '0': ['O', 'o'],        // Zero vs Letter O
        'O': ['0', 'o'],        // Letter O vs Zero
        'o': ['0', 'O'],        // Lowercase o vs Zero/O
        '1': ['l', 'I', '|'],   // One vs lowercase L vs uppercase I vs pipe
        'l': ['1', 'I', '|'],   // Lowercase L vs One
        'I': ['1', 'l', '|'],   // Uppercase I vs One/L
        
        // Cyrillic that looks like Latin
        'а': ['a'],  // Cyrillic а vs Latin a
        'е': ['e'],  // Cyrillic е vs Latin e
        'о': ['o'],  // Cyrillic о vs Latin o
        'р': ['p'],  // Cyrillic р vs Latin p
        'с': ['c'],  // Cyrillic с vs Latin c
        'у': ['y'],  // Cyrillic у vs Latin y
        'х': ['x'],  // Cyrillic х vs Latin x
        'н': ['h'],  // Cyrillic н vs Latin h
        
        // Multi-character confusables
        'rn': ['m'],  // "rn" can look like "m"
        'vv': ['w'],  // "vv" can look like "w"
        'cl': ['d'],  // "cl" can look like "d"
    };
    
    // Extract domain names without TLD for comparison
    const domainName = getDomainNameOnly(domain);
    const trustedName = getDomainNameOnly(trustedDomain);
    
    if (!domainName || !trustedName) return false;
    
    // Check for multi-character confusables first
    for (const [confusable, targets] of Object.entries(confusableMap)) {
        if (confusable.length > 1) {
            if (domainName.includes(confusable) && targets.some(target => 
                trustedName.includes(target) || trustedName === domainName.replace(confusable, target))) {
                return true;
            }
            if (trustedName.includes(confusable) && targets.some(target => 
                domainName.includes(target) || domainName === trustedName.replace(confusable, target))) {
                return true;
            }
        }
    }
    
    // Check single character confusables
    if (Math.abs(domainName.length - trustedName.length) > 1) return false;
    
    let confusableSubstitutions = 0;
    const maxLength = Math.max(domainName.length, trustedName.length);
    
    for (let i = 0; i < maxLength; i++) {
        const dChar = domainName[i] || '';
        const tChar = trustedName[i] || '';
        
        if (dChar !== tChar) {
            let isConfusable = false;
            
            // Check if characters are confusable
            if (confusableMap[dChar] && confusableMap[dChar].includes(tChar)) {
                isConfusable = true;
            } else if (confusableMap[tChar] && confusableMap[tChar].includes(dChar)) {
                isConfusable = true;
            }
            
            if (isConfusable) {
                confusableSubstitutions++;
            } else {
                // If it's not a confusable character, this is not a homoglyph attack
                return false;
            }
        }
    }
    
    // Only return true if ALL character differences are confusable substitutions
    return confusableSubstitutions > 0 && confusableSubstitutions <= 3;
};

const detectLookAlikeType = (domain, trustedDomain) => {
    const domainRoot = getRootDomain(domain);
    const trustedRoot = getRootDomain(trustedDomain);
    
    // Extract domain name without TLD for comparison
    const domainName = getDomainNameOnly(domainRoot);
    const trustedName = getDomainNameOnly(trustedRoot);
    
    if (!domainName || !trustedName) return null;
    
    // === PRIORITY 1: TLD-swap (highest priority - easy to detect) ===
    if (domainName === trustedName && domainRoot !== trustedRoot) {
        return 'TLD-swap';
    }
    
    // === PRIORITY 2: Subdomain (periods added within domain) ===
    // Check if removing dots from domain gives us the trusted domain
    const domainWithoutDots = domainRoot.replace(/\./g, '');
    const trustedWithoutDots = trustedRoot.replace(/\./g, '');
    if (domainWithoutDots === trustedWithoutDots && domainRoot !== trustedRoot) {
        return 'Subdomain';
    }
    
    // === PRIORITY 3: Hyphenation (hyphens added) ===
    const domainNoHyphens = domainName.replace(/-/g, '');
    const trustedNoHyphens = trustedName.replace(/-/g, '');
    if (domainNoHyphens === trustedNoHyphens && domainName !== trustedName) {
        return 'Hyphenation';
    }
    
    // === PRIORITY 4: Dictionary (common words appended) ===
    const commonExtensions = [
        'business', 'support', 'help', 'service', 'portal', 'app', 'web', 'site', 
        'online', 'shop', 'store', 'security', 'login', 'account', 'official',
        'secure', 'bank', 'company', 'corp', 'inc', 'group', 'team'
    ];
    
    for (const ext of commonExtensions) {
        // Check if domain is trusted + extension
        if (domainName === trustedName + ext || domainName === trustedName + '-' + ext) {
            return 'Dictionary';
        }
        // Check if domain is extension + trusted
        if (domainName === ext + trustedName || domainName === ext + '-' + trustedName) {
            return 'Dictionary';
        }
    }
    
    // === PRIORITY 5: Homoglyph (visually similar characters) ===
    if (detectHomoglyph(domain, trustedDomain)) {
        return 'Homoglyph';
    }
    
    // === PRIORITY 6: Addition (extra characters) ===
    if (domainName.length > trustedName.length) {
        // Simple addition check: domain contains trusted name as substring
        if (domainName.includes(trustedName)) {
            const extraChars = domainName.replace(trustedName, '');
            // Only classify as addition if extra chars are simple (not complex modifications)
            if (extraChars.length <= 3 && /^[a-z0-9-]*$/.test(extraChars)) {
                return 'Addition';
            }
        }
    }
    
    // === PRIORITY 7: Omission (missing characters) ===
    if (domainName.length < trustedName.length) {
        // Check if domain is trusted name with some characters removed
        if (trustedName.includes(domainName)) {
            const missingChars = trustedName.replace(domainName, '');
            // Only classify as omission if missing chars are simple
            if (missingChars.length <= 3 && /^[a-z0-9-]*$/.test(missingChars)) {
                return 'Omission';
            }
        }
    }
    
    // === PRIORITY 8: Replacement (character substitution) ===
    const editDistance = calculateLevenshteinDistance(domainName, trustedName);
    if (editDistance >= 1 && editDistance <= 2 && Math.abs(domainName.length - trustedName.length) <= 1) {
        return 'Replacement';
    }
    
    // === PRIORITY 9: Various (only for truly ambiguous cases) ===
    // Only use Various if there's some similarity but doesn't fit other categories
    if (editDistance >= 3 && editDistance <= 4 && domainName.length >= 4 && trustedName.length >= 4) {
        // Check if domains share significant common substrings
        const minLength = Math.min(domainName.length, trustedName.length);
        let commonChars = 0;
        
        for (let i = 0; i < minLength; i++) {
            if (domainName.includes(trustedName[i])) commonChars++;
        }
        
        // If more than 60% of characters are shared, classify as Various
        if (commonChars / minLength > 0.6) {
            return 'Various';
        }
    }
    
    return null;
};

// Levenshtein distance calculation for character replacement detection
const calculateLevenshteinDistance = (str1, str2) => {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
            if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1,     // insertion
                    matrix[i - 1][j] + 1      // deletion
                );
            }
        }
    }
    
    return matrix[str2.length][str1.length];
};

// Comprehensive look-alike domain checker
const checkLookAlikeDomain = (domain, trustedDomains) => {
    // Skip if domain is already trusted
    if (isDomainTrustedMatch(domain, trustedDomains)) {
        return null;
    }
    
    for (const trustedDomain of trustedDomains) {
        const attackType = detectLookAlikeType(domain, trustedDomain);
        
        if (attackType) {
            const attackInfo = SAFE_LINK_CONFIG.lookAlikeAttackTypes[attackType];
            if (attackInfo) {
                return {
                    isLookAlike: true,
                    attackType,
                    similarDomain: trustedDomain,
                    riskLevel: attackInfo.riskLevel,
                    reason: attackInfo.description,
                    explanation: attackInfo.explanation
                };
            }
        }
    }
    
    return null;
};

// Enhanced risk assessment with iOdin platform integration
export const getDomainRiskLevel = (url, securityContext = null) => {
    if (!url) return 'high';
    
    try {
        const fullUrl = url.startsWith('http') ? url : `https://${url}`;
        const urlObj = new URL(fullUrl);
        const domain = urlObj.hostname.toLowerCase();
        const pathname = urlObj.pathname.toLowerCase();
        const port = urlObj.port;
        
        // Security context integration - this is the key enhancement
        if (securityContext) {
            const securityRisk = assessSecurityContext(domain, securityContext);
            if (securityRisk.level !== 'unknown') {
                return securityRisk.level;
            }
        }
        
        // Enhanced checks
        if (url.length > 2000) return 'high';
        if (port && !['80', '443', ''].includes(port)) return 'high';
        
        const subdomains = domain.split('.');
        if (subdomains.length > 4) return 'high';
        
        // Check global trusted domains (with enhanced matching)
        if (isDomainTrustedMatch(domain, SAFE_LINK_CONFIG.globalTrustedDomains)) {
            return 'low';
        }
        
        // Check for high-risk patterns
        const highRiskPattern = SAFE_LINK_CONFIG.dangerousPatterns.find(item => 
            item.risk === 'high' && item.pattern.test(domain));
        if (highRiskPattern) {
            return 'high';
        }
        
        // Check file extensions
        if (SAFE_LINK_CONFIG.riskyExtensions.some(ext => 
            pathname.includes(ext.toLowerCase()))) {
            return 'high';
        }
        
        // Check suspicious TLDs
        if (SAFE_LINK_CONFIG.suspiciousTlds.some(tld => domain.endsWith(tld))) {
            return 'high';
        }
        
        // Check for medium-risk patterns
        const mediumRiskPattern = SAFE_LINK_CONFIG.dangerousPatterns.find(item => 
            item.risk === 'medium' && item.pattern.test(domain));
        if (mediumRiskPattern) {
            return 'medium';
        }
        
        // Default to medium for unknown domains
        return 'medium';
        
    } catch (e) {
        return 'high';
    }
};

// New function to assess security context from iOdin data
const assessSecurityContext = (domain, securityContext) => {
    const {
        trustedDomains = [],
        danglingSubdomains = [],
        lookAlikeDomains = []
        // SSL and Web Technology data - COMMENTED OUT
        // sslCertificates = [],
        // webTechnologies = []
    } = securityContext;
    
    // Check if domain is in client's trusted domains (with enhanced matching)
    const isDomainTrusted = isDomainTrustedMatch(domain, trustedDomains);
    
    // Check for dangling subdomains - HIGH RISK (also with enhanced matching)
    const isDangling = danglingSubdomains.some(danglingDomain => {
        const normalizedDanglingDomain = normalizeDomain(danglingDomain);
        const normalizedDomain = normalizeDomain(domain);
        
        return domain === danglingDomain || 
               normalizedDomain === normalizedDanglingDomain ||
               domain.endsWith('.' + danglingDomain) || 
               domain.endsWith('.' + normalizedDanglingDomain);
    });
    
    if (isDangling) {
        return {
            level: 'high',
            reason: 'Dangling subdomain detected - potential takeover risk',
            details: 'This subdomain is dangling and could be exploited by attackers'
        };
    }
    
    // Check for look-alike domains from scan results (HIGH risk)
    if (lookAlikeDomains && Array.isArray(lookAlikeDomains)) {
        const lookAlikeThreat = lookAlikeDomains.find(item => {
            const scanDomain = item?.domain?.toLowerCase();
            return scanDomain === domain;
        });
        
        if (lookAlikeThreat && lookAlikeThreat.type) {
            const attackConfig = SAFE_LINK_CONFIG.lookAlikeAttackTypes[lookAlikeThreat.type];
            if (attackConfig) {
                return {
                    level: attackConfig.riskLevel,
                    reason: `Look-alike domain attack detected`,
                    details: `Attack Type: ${lookAlikeThreat.type} - ${attackConfig.explanation}`,
                    attackType: lookAlikeThreat.type,
                    similarDomain: lookAlikeThreat.similarTo || 'Unknown target'
                };
            }
        }
    }
    
    // Advanced look-alike domain detection using pattern matching - HIGH/MEDIUM RISK
    const lookAlikeCheck = checkLookAlikeDomain(domain, trustedDomains);
    if (lookAlikeCheck) {
        return {
            level: lookAlikeCheck.riskLevel,
            reason: `Look-alike domain detected (${lookAlikeCheck.attackType})`,
            details: `${lookAlikeCheck.reason} - Similar to "${lookAlikeCheck.similarDomain}". ${lookAlikeCheck.explanation}`,
            attackType: lookAlikeCheck.attackType,
            similarDomain: lookAlikeCheck.similarDomain
        };
    }
    
    // Fallback: Check for simple TLD differences - MEDIUM RISK
    const similarDomainCheck = checkSimilarDomainTLD(domain, trustedDomains);
    if (similarDomainCheck) {
        return {
            level: 'medium',
            reason: 'Similar domain with different TLD detected',
            details: similarDomainCheck.reason
        };
    }
    
    // ========== SSL AND WEB TECHNOLOGY CHECKS - COMMENTED OUT ==========
    
    // Check for exploitable technology stack - HIGH RISK (check this first as it's more severe)
    // const exploitableTech = webTechnologies.find(item => {
    //     const techDomain = item?.subDomainName?.toLowerCase();
    //     const matches = techDomain === domain || domain.endsWith('.' + techDomain);
    //     return matches && 
    //            item?.hasVulnerabilities === true && 
    //            item?.exploits && 
    //            Object.values(item.exploits).some(exploit => exploit?.length > 0);
    // });
    
    // if (exploitableTech) {
    //     return {
    //         level: 'high',
    //         reason: 'Exploitable technology stack detected',
    //         details: 'This domain uses technology with known exploits'
    //     };
    // }
    
    // Check for vulnerable SSL certificates - MEDIUM RISK
    // const vulnerableSSL = sslCertificates.find(item => {
    //     const certDomain = item?.subDomainName?.toLowerCase();
    //     const matches = certDomain === domain || domain.endsWith('.' + certDomain);
    //     return matches && (
    //         item?.status !== 'Active' || 
    //         item?.expiresInDays < 30 ||
    //         item?.status_vulnerability
    //     );
    // });
    
    // if (vulnerableSSL) {
    //     let reason = 'SSL certificate issue detected';
    //     let details = '';
        
    //     if (vulnerableSSL.status !== 'Active') {
    //         reason = 'Inactive SSL certificate detected';
    //         details = `SSL certificate status: ${vulnerableSSL.status}`;
    //     } else if (vulnerableSSL.expiresInDays < 30) {
    //         reason = 'SSL certificate expiring soon';
    //         details = `Certificate expires in ${vulnerableSSL.expiresInDays} days`;
    //     } else if (vulnerableSSL.status_vulnerability) {
    //         reason = 'SSL certificate vulnerability detected';
    //         details = 'This certificate has known vulnerabilities';
    //     }
        
    //     return {
    //         level: 'medium',
    //         reason,
    //         details
    //     };
    // }
    
    // Check for vulnerable technology stack - MEDIUM RISK
    // const vulnerableTech = webTechnologies.find(item => {
    //     const techDomain = item?.subDomainName?.toLowerCase();
    //     const matches = techDomain === domain || domain.endsWith('.' + techDomain);
    //     return matches && item?.hasVulnerabilities === true;
    // });
    
    // if (vulnerableTech) {
    //     return {
    //         level: 'medium',
    //         reason: 'Vulnerable technology stack detected',
    //         details: 'This domain uses vulnerable technology'
    //     };
    // }
    
    // Check if SSL certificate is active and healthy - LOW RISK
    // const activeSSL = sslCertificates.find(item => {
    //     const certDomain = item?.subDomainName?.toLowerCase();
    //     const matches = certDomain === domain || domain.endsWith('.' + certDomain);
    //     return matches && 
    //            item?.status === 'Active' && 
    //            item?.expiresInDays >= 30 && 
    //            !item?.status_vulnerability;
    // });
    
    // Check if technology is OK (not vulnerable) - LOW RISK
    // const safeTech = webTechnologies.find(item => {
    //     const techDomain = item?.subDomainName?.toLowerCase();
    //     const matches = techDomain === domain || domain.endsWith('.' + techDomain);
    //     return matches && item?.hasVulnerabilities !== true;
    // });
    
    // ========== END COMMENTED OUT SECTION ==========
    
    // If domain is trusted and no vulnerabilities found
    if (isDomainTrusted) {
        return {
            level: 'low',
            reason: 'Trusted client domain',
            details: 'This domain belongs to your organization'
        };
    }
    
    return {
        level: 'unknown',
        reason: 'No security context available',
        details: null
    };
};

// Export the security context assessment function
export { assessSecurityContext };